import PageHeading from 'components/heading';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useApplicationCenter from 'state/applicationCenter/hooks/useApplicationCenter';
import { APPLICATION_CENTER } from 'state/applicationCenter/type';
import DocumentUpload from 'components/documentUploadApplicationCenter';
import { S3Response } from 'react-s3-uploader';
import { AxiosResponse } from 'axios';
import { apiOrgLive } from 'services/api';
import InputMultipleFileUpload, {
  getMultipleFileS3Props,
  IMultipleFileProps,
} from 'components/kyb/multipleUpload';
import { format } from 'date-fns';
import ToastContainer from 'components/toasts/container';
import NoDataAvailable from '../empty-state';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Icon } from 'components/ui/icon';
import { Banner } from 'components/ui/banner';

const DocumentManagement = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm();
  const {
    applicationCenter,
    toFetchDocuments,
    toUploadDocuments,
    deleteDocument,
  } = useApplicationCenter();

  const documents = applicationCenter.documents;
  const error = applicationCenter.error;

  const initialUpdatedDate: Record<string, string | undefined> = {
    [APPLICATION_CENTER.docType.dba_filing]: documents?.dba_filing?.updated_at,
    [APPLICATION_CENTER.docType.evidence_of_ein]:
      documents?.evidence_of_ein?.updated_at,
    [APPLICATION_CENTER.docType.signed_w_9]: documents?.signed_w_9?.updated_at,
    [APPLICATION_CENTER.docType.article_of_incorporation]:
      documents?.article_of_incorporation?.updated_at,
    [APPLICATION_CENTER.docType.formal_document_of_ownership]:
      documents?.formal_document_of_ownership?.updated_at,
    [APPLICATION_CENTER.docType.cannabis_license]:
      documents?.cannabis_license?.updated_at,
    [APPLICATION_CENTER.docType.rental_agreement]:
      documents?.rental_agreement?.updated_at,
    [APPLICATION_CENTER.docType.operating_procedures]:
      documents?.operating_procedures?.updated_at,
    [APPLICATION_CENTER.docType.account_statement]:
      documents?.account_statement &&
      documents.account_statement[0]?.updated_at,
    [APPLICATION_CENTER.docType.miscellaneous_doc]:
      documents?.miscellaneous_doc &&
      documents.miscellaneous_doc[0]?.updated_at,
    [APPLICATION_CENTER.docType.voided_check]:
      documents?.voided_check?.updated_at,
  };

  const [updatedAtDate, setUpdatedAtDate] = useState(initialUpdatedDate);

  const initialData: Record<string, APPLICATION_CENTER.Document> = {
    [APPLICATION_CENTER.docType.dba_filing]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.evidence_of_ein]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.signed_w_9]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.article_of_incorporation]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.formal_document_of_ownership]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.cannabis_license]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.rental_agreement]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.operating_procedures]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.account_statement]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.miscellaneous_doc]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
    [APPLICATION_CENTER.docType.voided_check]: {
      file: undefined,
      key: '',
      url: undefined,
      uploadId: undefined,
    },
  };

  const initialFieldConfig: Record<string, APPLICATION_CENTER.FieldConfig> = {
    [APPLICATION_CENTER.docType.dba_filing]: {
      id: APPLICATION_CENTER.docType.dba_filing,
      name: APPLICATION_CENTER.docType.dba_filing,
      label: 'Upload DBA filing (if applicable)',
      isRequired: false,
      shouldRender: true,
    },
    [APPLICATION_CENTER.docType.signed_w_9]: {
      id: APPLICATION_CENTER.docType.signed_w_9,
      name: APPLICATION_CENTER.docType.signed_w_9,
      isRequired: false,
      shouldRender: true,
      label: 'Signed W-9',
      sub_text:
        'The W-9 is an official form furnished by the IRS that contains information about your business. Please make sure you upload a signed copy of the W9.',
    },
    [APPLICATION_CENTER.docType.evidence_of_ein]: {
      id: APPLICATION_CENTER.docType.evidence_of_ein,
      name: APPLICATION_CENTER.docType.evidence_of_ein,
      isRequired: false,
      shouldRender: true,
      label: 'Evidence of EIN',
      sub_text:
        "IRS issued letter (SS4) confirming the applying entity's tax number (TIN/EIN). It should include the applying entity's name and the TIN/EIN of the applying entity.",
    },
    [APPLICATION_CENTER.docType.article_of_incorporation]: {
      id: APPLICATION_CENTER.docType.article_of_incorporation,
      name: APPLICATION_CENTER.docType.article_of_incorporation,
      isRequired: false,
      shouldRender: true,
      label: 'Articles of incorporation',
      sub_text:
        'Document issued by the relevant Secretary of State showing the name of the applying entity, the state of formation and incorporation ID.',
    },
    [APPLICATION_CENTER.docType.formal_document_of_ownership]: {
      id: APPLICATION_CENTER.docType.formal_document_of_ownership,
      name: APPLICATION_CENTER.docType.formal_document_of_ownership,
      isRequired: false,
      shouldRender: true,
      label: 'Formal document of ownership',
      sub_text:
        'Signed document which could be an Operating agreement for LLC, Stock certificate (for Public companies or Letter from Secretary of State (for Corporations).',
    },
    [APPLICATION_CENTER.docType.cannabis_license]: {
      id: APPLICATION_CENTER.docType.cannabis_license,
      name: APPLICATION_CENTER.docType.cannabis_license,
      isRequired: false,
      shouldRender: true,
      label: 'Cannabis license',
      sub_text:
        'Current state-issued cannabis license covering the permitted type of business, entity name, and address of the operating premises.',
    },
    [APPLICATION_CENTER.docType.operating_procedures]: {
      id: APPLICATION_CENTER.docType.operating_procedures,
      name: APPLICATION_CENTER.docType.operating_procedures,
      isRequired: false,
      shouldRender: true,
      label: 'Dispensary standard operating procedures',
      sub_text:
        'Detailed, written explanation of what processes need to be followed in the dispensary to accurately complete a task in a compliant and secure manner. You can view a sample template here.',
    },
    [APPLICATION_CENTER.docType.voided_check]: {
      id: APPLICATION_CENTER.docType.voided_check,
      name: APPLICATION_CENTER.docType.voided_check,
      isRequired: false,
      shouldRender: true,
      label: 'Voided check/Bank letter',
      sub_text:
        'Please upload either a voided check or a letter from your bank that contains your account information.',
    },
  };

  useEffect(() => {
    toFetchDocuments();
  }, []);

  const [documentDetails, setDocumentDetails] = useState(initialData);
  const [fieldConfig, setFieldConfig] = useState(initialFieldConfig);
  const [showStatus, setShowStatus] = useState(false);
  const [activeFile, setActiveFile] = useState<string>();
  const [fileStatus, setFileStatus] = useState('');
  const [percentUploaded, setPercentUploaded] = useState(0);
  const [accountStatementFileProps, setAccountStatementFileProps] =
    useState<IMultipleFileProps>();
  const [miscellaneousDocFileProps, setMiscellaneousDocFileProps] =
    useState<IMultipleFileProps>();
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isFormChanged, setIsFormChanged] = useState(false); // Track if the form has changed
  const [isMultipleFileCleared, setIsMultipleFileCleared] = useState<
    [boolean, string]
  >([false, '>']);

  // Function to compare the current state with the initial state
  const checkFormChange = (
    updatedDocumentDetails: Record<string, APPLICATION_CENTER.Document>
  ) => {
    const currentDocumentDetails = JSON.stringify(updatedDocumentDetails);
    // Check if current state is different from the initial state
    setIsFormChanged(currentDocumentDetails !== JSON.stringify(initialData));
  };

  const byteConverter = 1024 * 1024;
  const errorObject = {
    typeError: {
      message: 'Please upload a document of type PDF',
      type: 'custom',
    },
    sizeError: {
      message: 'Please upload a document of size less than 10MB.',
      type: 'custom',
    },
  };

  const updateDocumentObject = (
    type: string,
    value: APPLICATION_CENTER.Document
  ) => {
    documentDetails[type] = value;
    setDocumentDetails({ ...documentDetails });
    checkFormChange({ ...documentDetails }); // Check for changes every time the documentDetails is updated
  };

  // Track changes to documentDetails and enable/disable the button
  useEffect(() => {
    checkFormChange(documentDetails);
  }, [documentDetails]);

  // Track changes to documentDetails and enable/disable the button
  useEffect(() => {
    // Update documentDetails with the new file array for account_statement
    if (isMultipleFileCleared[0]) {
      updateDocumentObject(isMultipleFileCleared[1], {
        file: undefined,
        key: '',
        url: undefined,
        uploadId: undefined,
      });
    }
  }, [isMultipleFileCleared]);

  // check for size of file, type of file for drag and drop case and show errors accordingly
  const fileUpload = (file: File, fileType: APPLICATION_CENTER.docType) => {
    if (file.size > 10 * byteConverter) {
      const randomString = Math.random().toString(36);
      setError(fileType, {
        type: errorObject.sizeError.type,
        message: errorObject.sizeError.message,
      });
      const tempData = {
        file: undefined,
        key: randomString,
        url: undefined,
        uploadId: undefined,
      };
      updateDocumentObject(fileType, tempData);

      return false;
    }
    if (file.type !== 'application/pdf') {
      const randomString = Math.random().toString(36);
      setError(fileType, {
        type: errorObject.typeError.type,
        message: errorObject.typeError.message,
      });
      const tempData = {
        file: undefined,
        key: randomString,
        url: undefined,
        uploadId: undefined,
      };
      updateDocumentObject(fileType, tempData);

      return false;
    }

    return true;
  };

  // set file url when uploading is successful
  const setfileURL = (type: APPLICATION_CENTER.docType, fileURL: string) => {
    const tempData = {
      file: documentDetails[type].file,
      key: documentDetails[type].key,
      url: fileURL,
      uploadId: documentDetails[type].uploadId,
    };
    updateDocumentObject(type, tempData);
  };

  // get signing url from backend
  const getSignedUrl = async (
    file: File,
    callback: (params: S3Response) => void,
    fileType: APPLICATION_CENTER.docType
  ) => {
    clearErrors(fileType);

    if (fileUpload(file, fileType) === true) {
      const params = {
        file_name: `${file.name.split('.')[0]}_${fileType}.pdf`,
        file_type: file.type,
        file_size: file.size,
        name: fileType,
      };
      try {
        const response: AxiosResponse = await apiOrgLive.post(
          `/uploads/sign?kyb_id=${documents.kyb_id}`,
          params
        );
        const token = response.data.success_token;

        setfileURL(fileType, response.data.object_url);

        const data: S3Response = {
          signedUrl: response.data.signed_url,
          publicUrl: response.config.baseURL || '',
          filename: file.name,
          fileKey: token,
        };
        callback(data);
      } catch (e) {
        console.log(e);
      }
    } else {
      setShowStatus(false);
    }
  };

  // show success icon when uploading is completed
  const onUploadFinish = async (
    result: S3Response,
    file: File,
    fileType: APPLICATION_CENTER.docType
  ) => {
    const uploaded: AxiosResponse = await apiOrgLive.get(
      `/uploads/confirm?success_token=${result.fileKey}`
    );
    if (uploaded) {
      const tempData = {
        file,
        key: documentDetails[fileType].key,
        url: documentDetails[fileType].url,
        uploadId: uploaded.data?.id,
      };
      updateDocumentObject(fileType, tempData);
    }
  };

  // remove file from local state
  const clearFile = (formType: APPLICATION_CENTER.docType) => {
    const randomString = Math.random().toString(36);
    // this is to check if file is still in uploading state and user clears out the file
    // then we have to clear that status also
    if (!showStatus) {
      setShowStatus(false);
      deleteDocument(documentDetails[formType].uploadId);
      const tempData = {
        file: undefined,
        key: '',
        url: undefined,
        uploadId: undefined,
      };
      updateDocumentObject(formType, tempData);
      setValue(formType, undefined);
    }
  };

  const handleMultipleFileClear = () => {};

  const handleAccountStatementFileProps = (
    updatedFileProps: IMultipleFileProps | undefined
  ) => {
    // Update accountStatementFileProps
    setAccountStatementFileProps(updatedFileProps);

    // Update documentDetails with the new file array for account_statement
    updateDocumentObject(APPLICATION_CENTER.docType.account_statement, {
      file: undefined,
      key: 'account_statement',
      url: undefined,
      uploadId: undefined,
    });
  };

  const handleMiscellaneousDocFileProps = (
    updatedFileProps: IMultipleFileProps | undefined
  ) => {
    // Update miscellaneousDocFileProps
    setMiscellaneousDocFileProps(updatedFileProps);

    // Update documentDetails with the new file array for account_statement
    updateDocumentObject(APPLICATION_CENTER.docType.miscellaneous_doc, {
      file: undefined,
      key: 'miscellaneous_doc',
      url: undefined,
      uploadId: undefined,
    });
  };

  // upload fails on S3 bucket
  const onError = (message: string, fileType: APPLICATION_CENTER.docType) => {
    setShowStatus(false);
    clearFile(fileType);
    setError(fileType, {
      message: `${message} Please try again`,
    });
  };

  const onProgress = (
    percent: number,
    status: string,
    file: File,
    fileType: APPLICATION_CENTER.docType
  ) => {
    setActiveFile(fileType);
    setShowStatus(true);
    setFileStatus(status);
    setPercentUploaded(percent);
  };

  const banner = (
    <div className="mb-6 max-w-4xl">
      <Banner
        variant="info"
        message="All documents should be in pdf format (max. size 10MB)."
      />
    </div>
  );

  const lastUpdated = (
    lastUpdatedAtDate: Record<string, string | undefined>,
    item: string
  ) => {
    const updatedDate = lastUpdatedAtDate[item];
    return (
      updatedDate && (
        <div className="ml-4 pl-3 w-[269px] h-[52px]">
          <div className="text-gray-blue-60 text-xs font-medium leading-4">
            Last updated
          </div>
          <div className="text-gray-blue-60 text-sm font-normal leading-5">
            {updatedDate &&
              format(new Date(updatedDate), 'MMM dd, yyyy hh:mm a')}
          </div>
        </div>
      )
    );
  };

  useEffect(() => {
    setUpdatedAtDate({
      [APPLICATION_CENTER.docType.dba_filing]:
        documents?.dba_filing?.updated_at,
      [APPLICATION_CENTER.docType.evidence_of_ein]:
        documents?.evidence_of_ein?.updated_at,
      [APPLICATION_CENTER.docType.signed_w_9]:
        documents?.signed_w_9?.updated_at,
      [APPLICATION_CENTER.docType.article_of_incorporation]:
        documents?.article_of_incorporation?.updated_at,
      [APPLICATION_CENTER.docType.formal_document_of_ownership]:
        documents?.formal_document_of_ownership?.updated_at,
      [APPLICATION_CENTER.docType.cannabis_license]:
        documents?.cannabis_license?.updated_at,
      [APPLICATION_CENTER.docType.rental_agreement]:
        documents?.rental_agreement?.updated_at,
      [APPLICATION_CENTER.docType.operating_procedures]:
        documents?.operating_procedures?.updated_at,
      [APPLICATION_CENTER.docType.account_statement]:
        documents?.account_statement &&
        documents.account_statement[0]?.updated_at,
      [APPLICATION_CENTER.docType.miscellaneous_doc]:
        documents?.miscellaneous_doc &&
        documents.miscellaneous_doc[0]?.updated_at,
      [APPLICATION_CENTER.docType.voided_check]:
        documents?.voided_check?.updated_at,
    });
  }, [documents]);

  // make the loader invisible once the upload is complete
  useEffect(() => {
    let timer;
    clearTimeout(timer);
    if (fileStatus === 'Upload completed') {
      timer = setTimeout(() => setShowStatus(false), 3000);
    }
  }, [fileStatus]);

  const renderUploadField = (
    data: Record<string, APPLICATION_CENTER.Document>,
    item: string
  ) => {
    return (
      <div className="flex items-center">
        <div className="w-full max-w-4xl">
          <div className="mb-4">
            <DocumentUpload
              showStatus={showStatus}
              fieldConfig={fieldConfig}
              activeField={item}
              getSignedUrl={getSignedUrl}
              onUploadFinish={onUploadFinish}
              register={register}
              onError={onError}
              key={data[item].key}
              onProgress={onProgress}
              name={fieldConfig[item].name}
              uploadedFile={data[item].file}
              activeFile={activeFile}
              errors={errors}
              clearFile={clearFile}
              fileStatus={fileStatus}
              percentUploaded={percentUploaded}
              subLabel={fieldConfig[item].sub_text}
              required={fieldConfig[item].isRequired}
              multipleFiles={false}
            />
          </div>
        </div>
        {updatedAtDate && lastUpdated(updatedAtDate, item)}
      </div>
    );
  };

  const getFormSubmitData = (values: { [x: string]: FileList }) => {
    const formData: APPLICATION_CENTER.ISingleDocuments = {};

    Object.keys(values).forEach((item) => {
      if (values?.[item] && values[item].length > 0 && values[item][0].name) {
        const file = values[item][0]; // Get the first file from FileList

        formData[item as keyof APPLICATION_CENTER.ISingleDocuments] = {
          upload_id: documentDetails?.[item]?.uploadId || '', // Assuming you have `documentDetails` for `uploadId`
          name: file.name,
          url: documentDetails?.[item]?.url || '', // Assuming you have `documentDetails` for `url`
          type: file.type,
          updated_at: new Date().toISOString(),
        };
      }
    });

    return formData;
  };

  // submit form to backend
  const formSubmit = (values: { [x: string]: FileList }) => {
    const bankStmtDoc = getMultipleFileS3Props(
      accountStatementFileProps?.fileArray || []
    );

    const miscDoc = getMultipleFileS3Props(
      miscellaneousDocFileProps?.fileArray || []
    );

    const docs = getFormSubmitData(values);

    toUploadDocuments({
      kyb_id: documents.kyb_id,
      dba_filing: docs.dba_filing,
      signed_w_9: docs.signed_w_9,
      evidence_of_ein: docs.evidence_of_ein,
      article_of_incorporation: docs.article_of_incorporation,
      formal_document_of_ownership: docs.formal_document_of_ownership,
      cannabis_license: docs.cannabis_license,
      operating_procedures: docs.operating_procedures,
      voided_check: docs.voided_check,
      account_statement:
        bankStmtDoc.length > 0 ? bankStmtDoc : documents.account_statement,
      miscellaneous_doc:
        miscDoc.length > 0 ? miscDoc : documents.miscellaneous_doc,
    });
    setShowToast(true);
    setDocumentDetails(initialData);
  };

  return (
    <>
      <Card className="border-0 shadow-none p-0">
        <PageHeading
          heading="Document Management"
          subHeading="Upload payment application documents here"
          className="mb-5"
        />
        <hr className="-mx-9 my-6 border-t border-gray-blue-10" />
        {!error.getDocuments && documents ? (
          <>
            {banner}
            <div className="h-auto overflow-y-auto overflow-x-hidden w-full hide-scrollbars space-y-6">
              <form onSubmit={handleSubmit(formSubmit)}>
                {Object.keys(fieldConfig).map((item) => {
                  return fieldConfig[item].shouldRender ? (
                    renderUploadField(documentDetails, item)
                  ) : (
                    <React.Fragment key={item} />
                  );
                })}
                <div className="mb-4">
                  <Label
                    htmlFor="account_statement"
                    className="text-sm font-medium text-gray-blue-80 pb-1"
                  >
                    Last 3 months account statements from your primary banking
                    account.
                  </Label>
                  <p className="text-sm font-normal leading-5 text-gray-blue-60 max-w-4xl mb-2 pb-2">
                    If your bank account has been open for less than 3 months,
                    you can upload a letter from your bank that confirms your
                    account is open.
                  </p>
                  <p className="text-sm font-normal leading-5 text-gray-blue-60 max-w-4xl">
                    If uploading multiple files, please note, you can only
                    upload a maximum of 3 files. Choose and select all the files
                    at the same time, instead of uploading one by one.
                  </p>
                  <div className="flex items-center">
                    <div className="w-full max-w-4xl">
                      <InputMultipleFileUpload
                        elemId="account_statement"
                        errors={errors}
                        setValue={setValue}
                        setError={setError}
                        register={register}
                        multipleUpload={true}
                        validation={{}}
                        onBlur={() => {}}
                        fileProps={accountStatementFileProps?.fileArray || []}
                        setFileProps={handleAccountStatementFileProps}
                        setIsCleared={(isCleared: boolean) => {
                          setIsMultipleFileCleared([
                            isCleared,
                            APPLICATION_CENTER.docType.account_statement,
                          ]);
                        }}
                        kybId={documents.kyb_id}
                        fileLimit={3}
                      />
                    </div>
                    {lastUpdated(updatedAtDate, 'account_statement')}
                  </div>
                </div>
                <div className="mb-4">
                  <Label
                    htmlFor="miscellaneous_doc"
                    className="text-sm font-medium text-gray-blue-80 pb-1"
                  >
                    Miscellaneous/Others
                  </Label>
                  <div className="flex items-center">
                    <div className="w-full max-w-4xl">
                      <InputMultipleFileUpload
                        elemId="miscellaneous_doc"
                        errors={errors}
                        setValue={setValue}
                        setError={setError}
                        register={register}
                        multipleUpload={true}
                        validation={{}}
                        onBlur={() => {}}
                        fileProps={miscellaneousDocFileProps?.fileArray || []}
                        setFileProps={handleMiscellaneousDocFileProps}
                        setIsCleared={(isCleared: boolean) => {
                          setIsMultipleFileCleared([
                            isCleared,
                            APPLICATION_CENTER.docType.miscellaneous_doc,
                          ]);
                        }}
                        kybId={documents.kyb_id}
                        fileLimit={10}
                      />
                    </div>
                    {lastUpdated(updatedAtDate, 'miscellaneous_doc')}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={!isFormChanged}
                  className="px-12 mt-2"
                >
                  Save & Submit
                </Button>
              </form>
            </div>
          </>
        ) : (
          <NoDataAvailable />
        )}
      </Card>
      <ToastContainer
        showToast={showToast}
        onClose={() => setShowToast(false)}
        message={'Documents submitted successfully'}
      />
    </>
  );
};

export default DocumentManagement;